import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Radio } from 'ui/radio';
import { spacing } from 'ui/theme';
import WorkflowSelect from 'modules/workflowNodeEditors/components/TriggerWorkflowEditor/WorkflowSelect';
import { useSelector } from 'react-redux';
import { getWorkflow } from 'modules/workflows/ducks';
import { Link } from 'ui/links';
import { getEditWorkflowLink } from 'modules/workflowNodeEditors/components/TriggerWorkflowEditor/utils';
import { Rule, SendToJourneyMetadata } from '../types';

enum RADIO_VALUES {
  EXIT_CURRENT_JOURNEY = 'exit-current-journey',
  EXIT_CURRENT_JOURNEY_AND_GOTO_NEW_JOURNEY = 'exit-current-journey-and-goto-new-journey',
}

export const SEND_TO_JOURNEY_TEST_ID = 'send-to-journey';
export const EXIT_CURRENT_JOURNEY_TEST_ID = `${RADIO_VALUES.EXIT_CURRENT_JOURNEY}-test-id`;
export const EXIT_CURRENT_JOURNEY_AND_GOTO_NEW_JOURNEY_TEST_ID = `${RADIO_VALUES.EXIT_CURRENT_JOURNEY_AND_GOTO_NEW_JOURNEY}-test-id`;

const SendToJourneyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: ${spacing.base};
  gap: ${spacing.threeQuarter};
`;

const RadioRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing.base};
`;

const SelectedJourneyContainer = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.half};
`;

const StyldLink = styled(Link)`
  width: 100%;
`;

const SendToJourneyRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.threeQuarter};
`;

export type SendToJourneyProps = {
  rule: Rule;
  onSelect: (sendToJourneyMetadata: SendToJourneyMetadata | null) => void;
};

export const SendToJourney = ({ rule, onSelect }: SendToJourneyProps) => {
  const workflow = useSelector(getWorkflow);

  const { destinationId } = rule?.sendToJourneyMetadata || {};

  const [currentSelected, setCurrentSelected] = useState<RADIO_VALUES>(
    destinationId
      ? RADIO_VALUES.EXIT_CURRENT_JOURNEY_AND_GOTO_NEW_JOURNEY
      : RADIO_VALUES.EXIT_CURRENT_JOURNEY,
  );

  const isRadioButtonDisabled = rule?.childRules?.length === 0;

  // This will reset the destinationId if the user has removed all of the exit rules on the form, so it is in the original state.
  useEffect(() => {
    if (destinationId && isRadioButtonDisabled) {
      onSelect(null);
      setCurrentSelected(RADIO_VALUES.EXIT_CURRENT_JOURNEY);
    }
  }, [destinationId, isRadioButtonDisabled]);

  const handleOnClick = (currentSelectedValue: RADIO_VALUES) => {
    if (currentSelectedValue === RADIO_VALUES.EXIT_CURRENT_JOURNEY) {
      onSelect(null);
    }
    setCurrentSelected(currentSelectedValue);
  };

  return (
    <SendToJourneyContainer data-test={SEND_TO_JOURNEY_TEST_ID}>
      <RadioRow>
        <Radio
          isDisabled={isRadioButtonDisabled}
          isChecked={RADIO_VALUES.EXIT_CURRENT_JOURNEY === currentSelected}
          value={RADIO_VALUES.EXIT_CURRENT_JOURNEY === currentSelected}
          id={RADIO_VALUES.EXIT_CURRENT_JOURNEY}
          data-test={EXIT_CURRENT_JOURNEY_TEST_ID}
          onClick={() => handleOnClick(RADIO_VALUES.EXIT_CURRENT_JOURNEY)}
          size={24}
        />
        <div>User exits from this journey</div>
      </RadioRow>

      <RadioRow>
        <Radio
          isDisabled={isRadioButtonDisabled}
          isChecked={RADIO_VALUES.EXIT_CURRENT_JOURNEY_AND_GOTO_NEW_JOURNEY === currentSelected}
          value={RADIO_VALUES.EXIT_CURRENT_JOURNEY_AND_GOTO_NEW_JOURNEY === currentSelected}
          id={RADIO_VALUES.EXIT_CURRENT_JOURNEY_AND_GOTO_NEW_JOURNEY}
          data-test={EXIT_CURRENT_JOURNEY_AND_GOTO_NEW_JOURNEY_TEST_ID}
          onClick={() => handleOnClick(RADIO_VALUES.EXIT_CURRENT_JOURNEY_AND_GOTO_NEW_JOURNEY)}
          size={24}
        />
        <SendToJourneyRow>
          <div>User exits this journey and is sent to another journey</div>
          {RADIO_VALUES.EXIT_CURRENT_JOURNEY_AND_GOTO_NEW_JOURNEY === currentSelected && (
            <SelectedJourneyContainer>
              <WorkflowSelect
                showStatusInTrigger
                onlyPublished
                workflow={workflow}
                value={destinationId ? `${destinationId}` : destinationId}
                onChange={(value) => {
                  if (value) onSelect({ destinationId: value?.id.toString() });
                }}
              />
              {destinationId && (
                <StyldLink
                  hasOpenNewTabIcon
                  target="_blank"
                  href={getEditWorkflowLink(destinationId)}
                  rel="noreferrer noopener"
                  data-test="open-journey-link"
                >
                  Open journey
                </StyldLink>
              )}
            </SelectedJourneyContainer>
          )}
        </SendToJourneyRow>
      </RadioRow>
    </SendToJourneyContainer>
  );
};
